import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileSpreadsheet, Loader2, MapPinned, Plus, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { carteSubnav } from "@/lib/subnavs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, api, errMsg } from "@/lib/api";
import { textOn } from "@/lib/mapStyle";
import { PredecoupagePanel } from "@/components/PredecoupagePanel";
import { useAuth } from "@/context/AuthContext";
import { peut } from "@/lib/permissions";

function TourneeRow({ t, onSaved, onDeleted, onAddCp, onDelCp, droits }) {
  const ro = !droits.modifier;
  const [form, setForm] = useState(t);
  const [newCp, setNewCp] = useState("");
  const [confirm, setConfirm] = useState(false);
  useEffect(() => setForm(t), [t]);
  const dirty = ["libelle", "secteur", "sst", "couleur"].some((k) => (form[k] || "") !== (t[k] || ""));

  const save = async () => {
    try {
      const { data } = await api.put(`/tournees/${t.code}`, { libelle: form.libelle, secteur: form.secteur, sst: form.sst, couleur: form.couleur, hors_carte: t.hors_carte });
      onSaved({ ...t, ...data });
      toast.success(`Tournée ${t.code} enregistrée`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete(`/tournees/${t.code}`); onDeleted(t.code); toast.success(`Tournée ${t.code} supprimée`); }
    catch (e) { toast.error(errMsg(e)); setConfirm(false); }
  };

  return (
    <TableRow data-testid={`row-tournee-${t.code}`}>
      <TableCell className="w-14">
        <label className={`relative block w-9 h-9 rounded-md overflow-hidden border border-border ${ro ? "" : "cursor-pointer"}`} style={{ background: form.couleur }} title={ro ? undefined : "Changer la couleur"}>
          <input type="color" disabled={ro} value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="absolute inset-0 opacity-0 cursor-pointer" />
        </label>
      </TableCell>
      <TableCell className="font-mono font-bold text-base">{t.code}</TableCell>
      <TableCell className="min-w-[220px]"><Input value={form.libelle} readOnly={ro} onChange={(e) => setForm({ ...form, libelle: e.target.value })} className="h-8" /></TableCell>
      <TableCell className="min-w-[120px]"><Input value={form.secteur} readOnly={ro} onChange={(e) => setForm({ ...form, secteur: e.target.value })} className="h-8" list="secteurs-list" /></TableCell>
      <TableCell className="min-w-[130px]"><Input value={form.sst} readOnly={ro} onChange={(e) => setForm({ ...form, sst: e.target.value })} className="h-8" list="ssts-list" /></TableCell>
      <TableCell className="min-w-[200px]">
        <div className="flex flex-wrap gap-1 items-center">
          {t.codes_postaux.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1 font-mono text-xs pl-1.5 pr-0.5 py-0.5 rounded border border-border" title={a.libelle}>
              {a.cp}
              {droits.affecter && <button onClick={() => onDelCp(a)} className="p-0.5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive" aria-label={`Retirer ${a.cp}`}><X size={11} /></button>}
            </span>
          ))}
          {droits.affecter && <form onSubmit={(e) => { e.preventDefault(); if (/^\d{5}$/.test(newCp)) { onAddCp(t.code, newCp); setNewCp(""); } }} className="inline-flex">
            <input value={newCp} onChange={(e) => setNewCp(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="+ CP" className="w-14 h-6 text-xs font-mono px-1.5 rounded border border-dashed border-border bg-transparent" />
          </form>}
        </div>
        {t.hors_carte && <p className="text-[10px] text-muted-foreground mt-1">Hors carte (pas de zone géographique)</p>}
      </TableCell>
      <TableCell className="text-center text-sm">{t.nb_zones}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <div className="flex justify-end gap-1">
          {dirty && !ro && <Button size="sm" onClick={save}><Save size={14} /></Button>}
          {!t.hors_carte && (
            <Button size="sm" variant="ghost" asChild title="Voir sur la carte"><Link to={`/carte?tournee=${t.code}`}><MapPinned size={14} /></Link></Button>
          )}
          {droits.creer && (confirm ? (
            <Button size="sm" variant="destructive" onClick={del}>Confirmer</Button>
          ) : (
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setConfirm(true)} title="Supprimer"><Trash2 size={14} /></Button>
          ))}
        </div>
      </TableCell>
    </TableRow>
  );
}

function ImportTab({ onImported }) {
  const [file, setFile] = useState(null);
  const [feuilles, setFeuilles] = useState([]);
  const [feuille, setFeuille] = useState("");
  const [apercu, setApercu] = useState(null);
  const [busy, setBusy] = useState(false);

  const pick = async (f) => {
    setFile(f); setApercu(null); setFeuilles([]); setFeuille("");
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/import/feuilles", fd);
      setFeuilles(data.feuilles);
      const def = data.feuilles.find((s) => s.trim().toUpperCase() === "11A") || data.feuilles.find((s) => /code?\s*post.*11a/i.test(s)) || data.feuilles[0];
      setFeuille(def);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const preview = async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("feuille", feuille);
    setBusy(true);
    try { const { data } = await api.post("/import/apercu", fd); setApercu(data); }
    catch (e) { toast.error(errMsg(e)); setApercu(null); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("feuille", feuille);
    setBusy(true);
    try {
      const { data } = await api.post("/import/appliquer", fd);
      if (data.type === "communes") {
        toast.success(`${data.nb_communes} villages rattachés à ${data.nb_tournees} tournées${data.tournees_creees?.length ? ` (nouvelles : ${data.tournees_creees.join(", ")})` : ""}`);
      } else {
        toast.success(`${data.nb_tournees} tournées et ${data.nb_affectations} codes postaux importés`);
      }
      if (data.zones_orphelines) toast.warning(`${data.zones_orphelines} zone(s) pointent vers une tournée absente du nouveau fichier.`);
      setApercu(null); setFile(null); setFeuilles([]);
      onImported();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div>
          <p className="font-semibold flex items-center gap-2"><FileSpreadsheet size={18} /> Importer un fichier Excel</p>
          <p className="text-sm text-muted-foreground mt-1">
            Deux formats sont reconnus automatiquement :<br />
            • <b>par village</b> (feuille « 11A » : <b>Code_postal</b>, <b>Ville</b>, <b>n° tournée</b>) — chaque village est rattaché à sa tournée, prioritaire sur le code postal ;<br />
            • <b>par code postal</b> (feuille « codepost 11A » : <b>cod post</b>, <b>libellé tournée</b>, <b>SECTOR</b>, <b>stt</b>, <b>tournée</b>) — lignes « pts … » ignorées.<br />
            Les zones dessinées, les villages affectés à la main et les couleurs sont toujours conservés.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label>Fichier (.xlsx)</Label>
            <Input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid="import-file" />
          </div>
          {feuilles.length > 0 && (
            <div className="sm:w-56 space-y-1.5">
              <Label>Feuille</Label>
              <Select value={feuille} onValueChange={(v) => { setFeuille(v); setApercu(null); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{feuilles.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={preview} disabled={!file || !feuille || busy}>{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Aperçu</Button>
        </div>
      </div>

      {apercu && apercu.type === "communes" && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid="apercu-communes">
          <div className="flex flex-wrap gap-6">
            <div><p className="text-2xl font-bold">{apercu.affectations.length}</p><p className="text-xs text-muted-foreground">villages reconnus</p></div>
            <div><p className="text-2xl font-bold">{apercu.tournees.length}</p><p className="text-xs text-muted-foreground">tournées</p></div>
            <div><p className="text-2xl font-bold">{apercu.localites.length}</p><p className="text-xs text-muted-foreground">localités / hameaux</p></div>
          </div>
          {apercu.tournees.some((t) => t.nouvelle) && (
            <p className="text-xs text-amber-500">Tournées créées automatiquement : {apercu.tournees.filter((t) => t.nouvelle).map((t) => t.code).join(", ")}</p>
          )}
          {apercu.partagees.length > 0 && (
            <p className="text-xs text-muted-foreground">À découper en zones (plusieurs tournées) : {apercu.partagees.map((l) => `${l.ville} (${l.tournee})`).join(", ")}</p>
          )}
          {apercu.localites.length > 0 && (
            <p className="text-xs text-muted-foreground">Localités qui ne sont pas des communes — à dessiner en zone si leur tournée diffère de la commune : {apercu.localites.map((l) => `${l.ville} (${l.tournee})`).join(", ")}</p>
          )}
          {apercu.conflits.length > 0 && (
            <p className="text-xs text-amber-500">Communes fusionnées avec des tournées différentes (première ligne retenue) : {apercu.conflits.map((c) => `${c.commune} (${c.tournees.join("/")})`).join(", ")}</p>
          )}
          <div className="max-h-80 overflow-auto rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>Tournée</TableHead><TableHead>Villages</TableHead><TableHead>Communes</TableHead><TableHead>SST</TableHead></TableRow></TableHeader>
              <TableBody>
                {apercu.tournees.map((t) => (
                  <TableRow key={t.code}>
                    <TableCell className="font-mono font-semibold">{t.code}{t.nouvelle && <span className="text-[10px] text-amber-500 ml-1">nouvelle</span>}</TableCell>
                    <TableCell className="text-sm">{t.libelle}</TableCell>
                    <TableCell className="text-sm">{t.nb_communes}</TableCell>
                    <TableCell className="text-sm">{t.sst}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button onClick={apply} disabled={busy} data-testid="import-apply">{busy && <Loader2 size={14} className="animate-spin" />} Rattacher les villages à leurs tournées</Button>
        </div>
      )}

      {apercu && apercu.type !== "communes" && (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div className="flex flex-wrap gap-6">
            <div><p className="text-2xl font-bold">{apercu.tournees.length}</p><p className="text-xs text-muted-foreground">tournées</p></div>
            <div><p className="text-2xl font-bold">{apercu.affectations.length}</p><p className="text-xs text-muted-foreground">lignes code postal</p></div>
            <div><p className="text-2xl font-bold">{apercu.ignorees.length}</p><p className="text-xs text-muted-foreground">lignes « pts » ignorées</p></div>
          </div>
          {apercu.ignorees.length > 0 && <p className="text-xs text-muted-foreground">Ignorées : {apercu.ignorees.map((i) => `${i.libelle} (${i.tournee})`).join(", ")}</p>}
          {apercu.cp_sans_contour.length > 0 && (
            <p className="text-xs text-amber-500">Codes postaux sans commune connue en Occitanie : {apercu.cp_sans_contour.join(", ")}</p>
          )}
          <div className="max-h-80 overflow-auto rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>Tournée</TableHead><TableHead>Libellé</TableHead><TableHead>Secteur</TableHead><TableHead>SST</TableHead></TableRow></TableHeader>
              <TableBody>
                {apercu.tournees.map((t) => (
                  <TableRow key={t.code}>
                    <TableCell className="font-mono font-semibold">{t.code}</TableCell>
                    <TableCell className="text-sm">{t.libelle}{t.hors_carte && <span className="text-xs text-muted-foreground"> (hors carte)</span>}</TableCell>
                    <TableCell className="text-sm">{t.secteur}</TableCell>
                    <TableCell className="text-sm">{t.sst}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button onClick={apply} disabled={busy} data-testid="import-apply">{busy && <Loader2 size={14} className="animate-spin" />} Remplacer les tournées par ce fichier</Button>
        </div>
      )}
    </div>
  );
}

export default function TourneesPage() {
  const { user } = useAuth();
  const droits = {
    creer: peut(user, "carte_tournee_creer"), modifier: peut(user, "carte_tournee_modifier"),
    affecter: peut(user, "carte_affecter"), dessin: peut(user, "carte_dessin"),
    decoupage: peut(user, "carte_decoupage"), import: peut(user, "carte_import"),
  };
  const [tournees, setTournees] = useState([]);
  const [meta, setMeta] = useState({});
  const [metaCommunes, setMetaCommunes] = useState({});
  const [zones, setZones] = useState([]);
  const [voirPred, setVoirPred] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [nouvelle, setNouvelle] = useState({ code: "", libelle: "", secteur: "", sst: "" });

  const load = useCallback(async () => {
    try {
      const [t, z] = await Promise.all([api.get("/tournees"), api.get("/zones")]);
      setTournees(t.data.tournees); setMeta(t.data.import || {}); setMetaCommunes(t.data.import_communes || {}); setZones(z.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const secteurs = useMemo(() => [...new Set(tournees.map((t) => t.secteur).filter(Boolean))], [tournees]);
  const ssts = useMemo(() => [...new Set(tournees.map((t) => t.sst).filter(Boolean))], [tournees]);
  const zonesVisibles = voirPred ? zones : zones.filter((z) => !z.predecoupage);
  const tByCode = useMemo(() => Object.fromEntries(tournees.map((t) => [t.code, t])), [tournees]);

  const shown = tournees.filter((t) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return [t.code, t.libelle, t.secteur, t.sst, ...t.codes_postaux.map((a) => a.cp)].some((v) => (v || "").toLowerCase().includes(q));
  });

  const addCp = async (code, cp) => {
    try {
      const { data } = await api.post("/affectations", { cp, tournee: code });
      if (!data.communes_connues) toast.warning(`Aucune commune d'Occitanie connue pour ${cp}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delCp = async (a) => {
    try { await api.delete(`/affectations/${a.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tournees", nouvelle);
      toast.success(`Tournée ${nouvelle.code} créée`);
      setNouvelle({ code: "", libelle: "", secteur: "", sst: "" });
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const delZone = async (z) => {
    if (!window.confirm(`Supprimer la zone « ${z.nom} » ?`)) return;
    try { await api.delete(`/zones/${z.id}`); setZones((l) => l.filter((x) => x.id !== z.id)); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <AppShell title="Tournées & zones" subnav={carteSubnav()}>
      <datalist id="secteurs-list">{secteurs.map((s) => <option key={s} value={s} />)}</datalist>
      <datalist id="ssts-list">{ssts.map((s) => <option key={s} value={s} />)}</datalist>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Tournées & zones</h1>
          {meta.date && (
            <p className="text-sm text-muted-foreground">
              Codes postaux : {meta.source}{meta.feuille ? ` — feuille « ${meta.feuille} »` : ""} · {new Date(meta.date).toLocaleString("fr-FR")}
            </p>
          )}
          {metaCommunes.date && (
            <p className="text-sm text-muted-foreground">
              Villages : {metaCommunes.nb_communes} rattachés — {metaCommunes.source}{metaCommunes.feuille ? ` — feuille « ${metaCommunes.feuille} »` : ""} · {new Date(metaCommunes.date).toLocaleString("fr-FR")}
            </p>
          )}
        </div>
        {/* Export Excel : ville, code postal, n° de tournée, case vide, SST — à la couleur de la tournée */}
        <a href={`${API}/export/villages.xlsx`} download
          className="inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-medium border border-input hover:bg-accent" data-testid="export-villages">
          <Download size={15} /> Exporter les villages (Excel)
        </a>
      </div>

      <Tabs defaultValue="tournees">
        <TabsList>
          <TabsTrigger value="tournees">Tournées ({tournees.length})</TabsTrigger>
          <TabsTrigger value="zones">Zones ({zones.length})</TabsTrigger>
          {droits.import && <TabsTrigger value="import">Import Excel</TabsTrigger>}
        </TabsList>

        <TabsContent value="tournees" className="space-y-4 mt-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer (code, libellé, secteur, SST, CP)…" className="lg:w-80" />
            {droits.creer && <form onSubmit={create} className="flex flex-wrap gap-2 items-end lg:ml-auto">
              <Input value={nouvelle.code} onChange={(e) => setNouvelle({ ...nouvelle, code: e.target.value })} placeholder="Code" className="w-20" />
              <Input value={nouvelle.libelle} onChange={(e) => setNouvelle({ ...nouvelle, libelle: e.target.value })} placeholder="Libellé" className="w-40" />
              <Input value={nouvelle.secteur} onChange={(e) => setNouvelle({ ...nouvelle, secteur: e.target.value })} placeholder="Secteur" className="w-28" list="secteurs-list" />
              <Input value={nouvelle.sst} onChange={(e) => setNouvelle({ ...nouvelle, sst: e.target.value })} placeholder="SST" className="w-28" list="ssts-list" />
              <Button type="submit" disabled={!nouvelle.code.trim()}><Plus size={14} /> Ajouter</Button>
            </form>}
          </div>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            {loading ? <p className="p-4 text-sm text-muted-foreground">Chargement…</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Couleur</TableHead><TableHead>Code</TableHead><TableHead>Libellé</TableHead><TableHead>Secteur</TableHead>
                    <TableHead>SST</TableHead><TableHead>Codes postaux</TableHead><TableHead className="text-center">Zones</TableHead><TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shown.map((t) => (
                    <TourneeRow
                      key={t.code}
                      t={t}
                      onSaved={(nt) => setTournees((l) => l.map((x) => (x.code === nt.code ? nt : x)))}
                      onDeleted={(code) => setTournees((l) => l.filter((x) => x.code !== code))}
                      onAddCp={addCp}
                      onDelCp={delCp}
                      droits={droits}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="zones" className="mt-4">
          {droits.decoupage && <PredecoupagePanel onDone={load} />}
          <label className="flex items-center gap-2 text-sm mb-2 text-muted-foreground">
            <input type="checkbox" checked={voirPred} onChange={(e) => setVoirPred(e.target.checked)} className="accent-[hsl(var(--primary))]" />
            Afficher aussi les zones du pré-découpage dans la liste ({zones.filter((z) => z.predecoupage).length})
          </label>
          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            {zonesVisibles.length === 0 ? (
              <p className="p-5 text-sm text-muted-foreground">
                Aucune zone pour l'instant. Sur la <Link to="/carte" className="text-primary underline">carte</Link>, cliquez sur « Dessiner une zone ».
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead /><TableHead>Nom</TableHead><TableHead>Tournée</TableHead><TableHead>SST</TableHead><TableHead>Secteur</TableHead><TableHead>Surface</TableHead><TableHead>Modifiée</TableHead><TableHead /></TableRow>
                </TableHeader>
                <TableBody>
                  {zonesVisibles.map((z) => {
                    const t = tByCode[z.tournee];
                    return (
                      <TableRow key={z.id}>
                        <TableCell><span className="block w-5 h-5 rounded" style={{ background: z.couleur }} /></TableCell>
                        <TableCell className="font-medium">{z.nom}</TableCell>
                        <TableCell>
                          {z.tournee ? (
                            <span className="font-mono text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: t?.couleur || "#888", color: textOn(t?.couleur || "#888") }}>{z.tournee}</span>
                          ) : "—"}
                          {z.tournee && !t && <span className="text-xs text-destructive ml-2">tournée absente</span>}
                        </TableCell>
                        <TableCell className="text-sm">{z.sst || "—"}</TableCell>
                        <TableCell className="text-sm">{z.secteur || "—"}</TableCell>
                        <TableCell className="text-sm">{z.area_km2 < 1 ? `${Math.round(z.area_km2 * 100)} ha` : `${z.area_km2.toFixed(1)} km²`}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{z.updated_at ? new Date(z.updated_at).toLocaleDateString("fr-FR") : ""}{z.created_by ? ` · ${z.created_by}` : ""}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" asChild><Link to={`/carte?zone=${z.id}`}><MapPinned size={14} /></Link></Button>
                          {droits.dessin && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => delZone(z)}><Trash2 size={14} /></Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {droits.import && <TabsContent value="import" className="mt-4">
          <ImportTab onImported={load} />
        </TabsContent>}
      </Tabs>
    </AppShell>
  );
}
